/**
 * Derived announcement lifecycle state — never stored, always computed from
 * `active` + `isPublished` + the scheduling window as of `now`. Single state flag + a
 * window prevents the draft/scheduled/live/expired states from drifting out
 * of sync with each other (there's nothing to drift: it's recomputed every
 * read). Shared by the admin list's status column and anywhere else that
 * needs to describe an announcement's lifecycle state.
 */
export type AnnouncementStatus = 'inactive' | 'draft' | 'scheduled' | 'live' | 'expired';

export interface AnnouncementWindow {
  active: boolean;
  isPublished: boolean;
  startAt: string;
  endAt: string | null;
}

export function announcementStatus(
  a: AnnouncementWindow,
  now: Date = new Date(),
): AnnouncementStatus {
  // First: a deactivated (soft-deleted) row is never shown to anyone, even if
  // it's still flagged published and inside its window.
  if (!a.active) return 'inactive';
  if (!a.isPublished) return 'draft';
  const start = new Date(a.startAt);
  if (now < start) return 'scheduled';
  if (a.endAt && now >= new Date(a.endAt)) return 'expired';
  return 'live';
}

export const ANNOUNCEMENT_STATUS_LABELS: Record<AnnouncementStatus, string> = {
  inactive: 'Inactive',
  draft: 'Draft',
  scheduled: 'Scheduled',
  live: 'Live',
  expired: 'Expired',
};

/** `bg-*`/`text-*` pairs for a `PillBadge`, matching every other status
 *  column's colored-pill treatment in this app (e.g. `CustomerStatusBadge`). */
export const ANNOUNCEMENT_STATUS_COLOR_CLASSES: Record<AnnouncementStatus, string> = {
  inactive: 'bg-status-neutral text-status-neutral-foreground',
  draft: 'bg-status-neutral text-status-neutral-foreground',
  scheduled: 'bg-status-info text-status-info-foreground',
  live: 'bg-status-ok text-status-ok-foreground',
  expired: 'bg-status-neutral text-status-neutral-foreground',
};
